import {
    Alert,
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Avatar,
    Divider,
    Button,
    IconButton,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { useState, type ReactNode } from "react";
import { formationService, trainingService } from "../api/service.ts";
import { AssignedPlayer, Player, ScoredTeam, TrainingProposal, TrainingSession, TrainingSet } from "../api/types.tsx"
import TrainingResultDialog from "./TrainingResultDialog.tsx";


interface Props {
    proposal: TrainingProposal;
}

/** Set già giocato (flusso con panchina: composizione e punteggio registrati set per set) */
interface PlayedSet {
    teamA: Player[];
    teamB: Player[];
    bench: Player[];
    scoreA: number;
    scoreB: number;
}

// gruppo di appartenenza di un giocatore: squadra 0/1 oppure panchina
type Group = 0 | 1 | "bench";

export default function TrainingTeamsDialog({ proposal }: Props) {
    const [localTeams, setLocalTeams] = useState<ScoredTeam[]>(structuredClone(proposal.teams));
    const [bench, setBench] = useState<Player[]>(structuredClone(proposal.bench ?? []));
    const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [showResultsDialog, setShowResultsDialog] = useState(false);
    const [playedSets, setPlayedSets] = useState<PlayedSet[]>([]);
    const [score, setScore] = useState<{ a: string; b: string }>({ a: "", b: "" });
    const [error, setError] = useState<string | null>(null);
    const [teamA, teamB] = localTeams;

    // la panchina c'è quando i presenti superano 14: si registra set per set
    const benchMode = (proposal.bench?.length ?? 0) > 0;

    if (!proposal.teams || proposal.teams.length === 0) {
        return <Typography>Nessuna squadra generata</Typography>;
    }

    const groupOf = (player: Player): Group => {
        if (localTeams[0].team.some((ap) => ap.player.id === player.id)) return 0;
        if (localTeams[1].team.some((ap) => ap.player.id === player.id)) return 1;
        return "bench";
    };

    // 🔄 Selezione di due giocatori -> scambio (tra squadre o con la panchina)
    const handlePlayerClick = (player: Player) => {
        if (!selectedPlayer) {
            setSelectedPlayer(player);
            return;
        }
        if (selectedPlayer.id !== player.id) {
            swapPlayers(selectedPlayer, player);
        }
        setSelectedPlayer(null);
    };

    const roleFor = (incoming: Player, outgoing: AssignedPlayer) =>
        incoming.roles.includes(outgoing.role) ? outgoing.role : incoming.roles[0] ?? "BANDA";

    const swapPlayers = async (p1: Player, p2: Player) => {
        const g1 = groupOf(p1);
        const g2 = groupOf(p2);
        if (g1 === g2) return;

        const newTeams = localTeams.map((t) => ({ ...t, team: [...t.team] }));
        let newBench = [...bench];

        if (g1 !== "bench" && g2 !== "bench") {
            const i1 = newTeams[g1].team.findIndex((ap) => ap.player.id === p1.id);
            const i2 = newTeams[g2].team.findIndex((ap) => ap.player.id === p2.id);
            const temp = newTeams[g1].team[i1];
            newTeams[g1].team[i1] = newTeams[g2].team[i2];
            newTeams[g2].team[i2] = temp;
        } else {
            // uno dei due è in panchina: entra al posto dell'altro, con lo stesso ruolo se può
            const [benchPlayer, fieldPlayer, teamIdx] =
                g1 === "bench" ? [p1, p2, g2 as 0 | 1] : [p2, p1, g1 as 0 | 1];
            const idx = newTeams[teamIdx].team.findIndex((ap) => ap.player.id === fieldPlayer.id);
            const outgoing = newTeams[teamIdx].team[idx];
            newTeams[teamIdx].team[idx] = { player: benchPlayer, role: roleFor(benchPlayer, outgoing) };
            newBench = newBench.map((p) => (p.id === benchPlayer.id ? fieldPlayer : p));
        }

        const updated = await formationService.evaluateTeams(newTeams);
        setLocalTeams(updated);
        setBench(newBench);
    };

    // ↪️ Sposta un giocatore da una squadra all'altra
    const movePlayer = async (player: Player, fromTeamIndex: number) => {
        const toTeamIndex = fromTeamIndex === 0 ? 1 : 0;
        const newTeams = structuredClone(localTeams);

        const idx = newTeams[fromTeamIndex].team.findIndex((ap) => ap.player.id === player.id);
        if (idx !== -1) {
            const [removed] = newTeams[fromTeamIndex].team.splice(idx, 1);
            newTeams[toTeamIndex].team.push(removed);
            const updated = await formationService.evaluateTeams(newTeams);
            setLocalTeams(updated);
        }
    };

    const saveSession = async (sets: TrainingSet[]) => {
        try {
            setIsSaving(true);
            setError(null);
            const now = new Date().toISOString();
            const session: TrainingSession = {
                date: now,
                note: `Training session ${now.substring(0, 10)}`,
                sets,
            };
            await trainingService.saveTrainingSession(session);
            setSaved(true);
            console.log("✅ Session saved:", JSON.stringify(session));
        } catch (err) {
            console.error("❌ Errore durante il salvataggio sessione:", err);
            setError("Salvataggio non riuscito");
        } finally {
            setIsSaving(false);
        }
    };

    // 💾 Flusso classico: stesse squadre per tutti i set, risultati inseriti alla fine
    const handleConfirmResults = async (results: { setNumber: number; scoreA: number; scoreB: number; }[]) => {
        const teamAPlayers: Player[] = teamA.team.map(ap => ap.player);
        const teamBPlayers: Player[] = teamB.team.map(ap => ap.player);
        await saveSession(results.map((r) => ({
            setNumber: r.setNumber,
            scoreA: r.scoreA,
            scoreB: r.scoreB,
            teamA: teamAPlayers,
            teamB: teamBPlayers,
        })));
    };

    // ⏭️ Flusso con panchina: registra il set corrente e chiede al BE la rotazione per il prossimo
    const handleNextSet = async () => {
        const scoreA = Number(score.a);
        const scoreB = Number(score.b);
        const current: PlayedSet = {
            teamA: teamA.team.map((ap) => ap.player),
            teamB: teamB.team.map((ap) => ap.player),
            bench,
            scoreA,
            scoreB,
        };
        const allSets = [...playedSets, current];

        const played: Record<number, number> = {};
        allSets.forEach((s) =>
            [...s.teamA, ...s.teamB].forEach((p) => { played[p.id] = (played[p.id] ?? 0) + 1; }));

        const setters = [...teamA.team, ...teamB.team]
            .filter((ap) => ap.role === "PALLEGGIO")
            .map((ap) => ap.player.id);

        try {
            const next = await formationService.getNextTrainingSet({
                teamA: teamA.team.map((ap) => ({ playerId: ap.player.id, role: ap.role })),
                teamB: teamB.team.map((ap) => ({ playerId: ap.player.id, role: ap.role })),
                bench: bench.map((p) => p.id),
                setters,
                playedSets: played,
            });
            setPlayedSets(allSets);
            setLocalTeams(next.teams);
            setBench(next.bench);
            setScore({ a: "", b: "" });
            setError(null);
        } catch {
            setError("Impossibile calcolare la rotazione del prossimo set");
        }
    };

    const handleSaveBenchSession = async () => {
        await saveSession(playedSets.map((s, i) => ({
            setNumber: i + 1,
            scoreA: s.scoreA,
            scoreB: s.scoreB,
            teamA: s.teamA,
            teamB: s.teamB,
            bench: s.bench,
        })));
    };

    const scoreValid = score.a !== "" && score.b !== "" && Number(score.a) !== Number(score.b);

    const renderPlayerRow = (player: Player, right: ReactNode) => {
        const isSelected = selectedPlayer?.id === player.id;
        return (
            <Box
                key={player.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={0.5}
                sx={{
                    bgcolor: isSelected ? "action.selected" : "transparent",
                    borderRadius: 1,
                    px: 1,
                    cursor: "pointer",
                }}
                onClick={() => handlePlayerClick(player)}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar
                        sx={{
                            width: 28,
                            height: 28,
                            bgcolor: (theme) =>
                                player.gender === "M"
                                    ? theme.palette.info.main
                                    : theme.palette.secondary.light,
                            fontSize: "0.8rem",
                        }}
                    >
                        {player.nickname.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2">
                        {player.nickname} ({player.gender})
                    </Typography>
                </Box>
                {right}
            </Box>
        );
    };

    const renderTeam = (team: ScoredTeam, label: string, index: number) => (
        <Card
            elevation={3}
            sx={{
                flex: 1,
                borderRadius: 2,
                backgroundColor: "background.paper",
                overflow: "hidden",
            }}
        >
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {label} ({team.team.length}) • Score {team.totalScore.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Attack: {team.attack.toFixed(1)} • Defense: {team.defense.toFixed(1)} • Serve:{" "}
                    {team.serve.toFixed(1)} • Wall: {team.wall.toFixed(1)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Skill avg: {team.avgSkillScore.toFixed(1)} • Balance: {team.balanceScore.toFixed(1)} • Gender:{" "}
                    {team.genderBalanceScore.toFixed(1)}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Age: {team.avgAge.toFixed(1)} • Seniority: {team.avgSeniority.toFixed(1)} • Personality:{" "}
                    {team.personality.toFixed(1)}
                </Typography>
                <Divider sx={{ my: 1 }} />
                {team.team.map((ap) =>
                    renderPlayerRow(ap.player, (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Chip label={ap.role} size="small" sx={{ textTransform: "uppercase" }} />
                            <Tooltip title="Move to other team">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        movePlayer(ap.player, index);
                                    }}
                                >
                                    <ArrowForwardIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))
                )}
            </CardContent>
        </Card>
    );

    const renderBench = () => (
        <Card elevation={1} sx={{ borderRadius: 2, flex: { md: "0 0 220px" }, bgcolor: "grey.50" }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>🪑 Panchina ({bench.length})</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Clicca un giocatore in panchina e poi uno in campo per scambiarli
                </Typography>
                <Divider sx={{ my: 1 }} />
                {bench.map((p) =>
                    renderPlayerRow(p, <Chip label={p.roles[0] ?? ""} size="small" variant="outlined" />)
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">Allenamento salvato</Alert>}

            {benchMode && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Set {playedSets.length + 1}
                </Typography>
            )}

            {/* squadre (+ panchina) */}
            <Box display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
                {renderTeam(teamA, "Team A", 0)}
                {renderTeam(teamB, "Team B", 1)}
                {benchMode && renderBench()}
            </Box>

            {benchMode ? (
                <>
                    {/* punteggio del set corrente + rotazione */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="flex-end">
                        <TextField label="Team A" type="number" size="small" sx={{ width: 100 }}
                            value={score.a} onChange={(e) => setScore((s) => ({ ...s, a: e.target.value }))} />
                        <TextField label="Team B" type="number" size="small" sx={{ width: 100 }}
                            value={score.b} onChange={(e) => setScore((s) => ({ ...s, b: e.target.value }))} />
                        <Button variant="outlined" startIcon={<SkipNextIcon />} disabled={!scoreValid || saved}
                            onClick={handleNextSet}>
                            Registra set e ruota panchina
                        </Button>
                    </Stack>

                    {playedSets.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Set registrati</Typography>
                            {playedSets.map((s, i) => (
                                <Typography key={i} variant="body2" color="text.secondary">
                                    Set {i + 1}: {s.scoreA} - {s.scoreB} • panchina: {s.bench.map((p) => p.nickname).join(", ")}
                                </Typography>
                            ))}
                        </Box>
                    )}

                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSaveBenchSession}
                            disabled={isSaving || saved || playedSets.length === 0}
                        >
                            {isSaving ? "Saving..." : `Salva allenamento (${playedSets.length} set)`}
                        </Button>
                    </Box>
                </>
            ) : (
                <>
                    {/* pulsante salva */}
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => setShowResultsDialog(true)}
                            disabled={isSaving}
                        >
                            {isSaving ? "Saving..." : "Save"}
                        </Button>
                    </Box>

                    {/* dialog inserimento risultati */}
                    <TrainingResultDialog
                        open={showResultsDialog}
                        onClose={() => setShowResultsDialog(false)}
                        onConfirm={handleConfirmResults}
                    />
                </>
            )}
        </Box>
    );
}
